"use client";

import { useEffect, useState } from "react";
import { Row, Col, Form, FloatingLabel } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { showLoading } from "react-global-loading";

import * as myApi from "../../Api";
import { toast } from "react-toastify";
import { validateEmail, validatePhone, validateOrganization, validateText } from "../../../utils/formValidation";

const SignUpForm = () => {

  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    phone: '',
    organization: '',
    name: '',
    passwd: '',
    passwd_confirmation: ''
  });

  const paperSubmissionExpiration = async() => {
    try {
      const response = await myApi.get('/deadline');
      if (response.data.result) {
        const deadlineInfo = response.data.result;
        const dateNow = new Date();
        const dateLimit = new Date(deadlineInfo.general_deadline);

        if (dateNow > dateLimit) {
          toast('Submission is closed', { type: "error" });
          setTimeout(() => {window.location.href = '/auth/sign-in/';}, 3000);
        }
      }
    } catch (error) {
      console.error('Error fetching deadline:', error);
      toast('Error checking submission deadline', { type: "error" });
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const passValue = formData.get("passwd");
    const confirmPassValue = formData.get("passwd_confirmation");

    // Validate all fields before submit
    const errors = {};
    
    // Validate name
    const nameValidation = validateText(formData.get("name"), { 
      minLength: 3, 
      maxLength: 100, 
      required: true, 
      fieldName: 'Full Name' 
    });
    if (!nameValidation.valid) errors.name = nameValidation.message;

    // Validate email
    const emailValidation = validateEmail(formData.get("email"));
    if (!emailValidation.valid) errors.email = emailValidation.message;

    // Validate organization
    const orgValidation = validateOrganization(formData.get("organization"));
    if (!orgValidation.valid) errors.organization = orgValidation.message;

    // Validate phone
    const phoneValidation = validatePhone(formData.get("handphone"));
    if (!phoneValidation.valid) errors.phone = phoneValidation.message;

    // Validate password
    if (!passValue || passValue.trim() === '') {
      errors.passwd = 'Password is required';
    } else if (passValue.length < 6) {
      errors.passwd = 'Password must be at least 6 characters';
    }

    // Validate password confirmation
    if (!confirmPassValue || confirmPassValue.trim() === '') {
      errors.passwd_confirmation = 'Please confirm your password';
    } else if (passValue !== confirmPassValue) {
      errors.passwd_confirmation = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast("Please fix the validation errors", { type: "error" });
      return;
    }

    setValidationErrors({});

    const data = {
      data_nama: formData.get("name"),
      data_email: formData.get("email"),
      data_handphone: formData.get("handphone"),
      data_organisasi: formData.get("organization"),
      data_password: formData.get("passwd"),
    };

    // register user
    myApi
      .post("/register", data)
      .then((response) => {
        if (response.data.result !== undefined) {
          const name = response.data.result.name;
          const email = response.data.result.email;
          const userIsActive = response.data.result.user_status;

          if (email !== null && userIsActive === "active") {
            toast("User sudah terdaftar. Silahkan login", { type: "error" });
          } else if (email !== null && userIsActive === "inactive") {
            toast("Email sudah terdaftar namun belum aktif. Silahkan cek email untuk aktivasi.", { type: "warning" });
            const data = {
              name: name,
              email: email
            }
            setTimeout(() => {
              showLoading(false);
              navigate("/auth/activation/", { state: data });
            }, 1500);
          } else {
            const data = {
              name: name,
              email: email
            }

            // Success toast is now handled by API interceptor
            // Hide loading before navigation
            setTimeout(() => {
              showLoading(false);
              navigate("/auth/activation/", { state: data });
            }, 1500);
          }
        }
      })
      .catch((error) => {
        toast(error.response?.data?.message || 'Registration failed', { type: "error" });
      });
  };

  useEffect(() => {
    paperSubmissionExpiration();
  }, []);

  return (
    <>
      <div className="auth-main-content m-auto m-1230 px-3">
        <Row className="align-items-center">
          <Col lg={6} className="d-none d-lg-block">
            <img
              src="/images/ejavec/side-03.jpg"
              className="rounded-3"
              alt="register"
              width={500}
              height={804}
            />
          </Col>

          <Col lg={6}>
            <div className="mw-480 ms-lg-auto">
              <div className="d-inline-block mb-4">
                <img
                  src="/images/ejavec/ejavec-logo.svg"
                  className="rounded-3 for-light-logo"
                  alt="login"
                  width={100}
                  height={26}
                />
                <img
                  src="/images/ejavec/ejavec-forum_sitelogo_2026.svg"
                  className="rounded-3 for-dark-logo"
                  alt="login"
                  width={100}
                  height={26}
                />
              </div>

              <h3 className="fs-28 mb-2">Sign up</h3>
              <p className="fw-medium fs-16 mb-4">
                Sign up with your email address
              </p>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <FloatingLabel
                    controlId="floatingInput"
                    label="Full Name"
                    className="mb-3"
                  >
                    <Form.Control
                      type="text"
                      name="name"
                      className={`h-55 ${validationErrors.name ? 'is-invalid' : ''}`}
                      placeholder="Enter your full name"
                      required
                      onBlur={(e) => {
                        const validation = validateText(e.target.value, { 
                          minLength: 3, 
                          maxLength: 100, 
                          required: true, 
                          fieldName: 'Full Name' 
                        });
                        setValidationErrors(prev => ({
                          ...prev,
                          name: validation.valid ? '' : validation.message
                        }));
                      }}
                    />
                    {validationErrors.name && (
                      <Form.Control.Feedback type="invalid">{validationErrors.name}</Form.Control.Feedback>
                    )}
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                  <FloatingLabel
                    controlId="floatingEmail"
                    label="Email Address"
                    className="mb-3"
                  >
                    <Form.Control
                      type="email"
                      name="email"
                      className={`h-55 ${validationErrors.email ? 'is-invalid' : ''}`}
                      placeholder="mail@example.com"
                      required
                      onBlur={(e) => {
                        const validation = validateEmail(e.target.value);
                        setValidationErrors(prev => ({
                          ...prev,
                          email: validation.valid ? '' : validation.message
                        }));
                      }}
                    />
                    {validationErrors.email && (
                      <Form.Control.Feedback type="invalid">{validationErrors.email}</Form.Control.Feedback>
                    )}
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                  <FloatingLabel
                    controlId="floatingOrganization"
                    label="Instansi / Organisasi / Universitas"
                    className="mb-3"
                  >
                  <Form.Control
                      type="text"
                      name="organization"
                      className={`h-55 ${validationErrors.organization ? 'is-invalid' : ''}`}
                      placeholder="Masukkan nama Instansi / Organisasi / Universitas"
                      required
                      onBlur={(e) => {
                        const validation = validateOrganization(e.target.value);
                        setValidationErrors(prev => ({
                          ...prev,
                          organization: validation.valid ? '' : validation.message
                        }));
                      }}
                    />
                    {validationErrors.organization && (
                      <Form.Control.Feedback type="invalid">{validationErrors.organization}</Form.Control.Feedback>
                    )}
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                  <FloatingLabel
                    controlId="floatingHandphone"
                    label="Phone Number"
                    className="mb-3"
                  >
                    <Form.Control
                      type="text"
                      name="handphone"
                      className={`h-55 ${validationErrors.phone ? 'is-invalid' : ''}`}
                      placeholder="Enter your phone number (e.g., 081234567890)"
                      required
                      onBlur={(e) => {
                        const validation = validatePhone(e.target.value);
                        setValidationErrors(prev => ({
                          ...prev,
                          phone: validation.valid ? '' : validation.message
                        }));
                      }}
                    />
                    {validationErrors.phone && (
                      <Form.Control.Feedback type="invalid">{validationErrors.phone}</Form.Control.Feedback>
                    )}
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                  <FloatingLabel
                    controlId="floatingPassword"
                    label="Password"
                    className="mb-3"
                  >
                    <Form.Control
                      type="password"
                      name="passwd"
                      className={`h-55 ${validationErrors.passwd ? 'is-invalid' : ''}`}
                      placeholder="Type password"
                      required
                      minLength={6}
                      onBlur={(e) => {
                        const value = e.target.value;
                        let error = '';
                        if (!value || value.trim() === '') {
                          error = 'Password is required';
                        } else if (value.length < 6) {
                          error = 'Password must be at least 6 characters';
                        }
                        setValidationErrors(prev => ({
                          ...prev,
                          passwd: error
                        }));
                      }}
                    />
                    {validationErrors.passwd && (
                      <Form.Control.Feedback type="invalid">{validationErrors.passwd}</Form.Control.Feedback>
                    )}
                  </FloatingLabel>
                  <FloatingLabel
                    controlId="floatingPasswordConfirmation"
                    label="Confirm Password"
                    className="mb-3"
                  >
                    <Form.Control
                      type="password"
                      name="passwd_confirmation"
                      className={`h-55 mt-3 ${validationErrors.passwd_confirmation ? 'is-invalid' : ''}`}
                      placeholder="Confirm password"
                      required
                      onBlur={(e) => {
                        const value = e.target.value;
                        const passValue = document.querySelector('input[name="passwd"]')?.value;
                        let error = '';
                        if (!value || value.trim() === '') {
                          error = 'Please confirm your password';
                        } else if (value !== passValue) {
                          error = 'Passwords do not match';
                        }
                        setValidationErrors(prev => ({
                          ...prev,
                          passwd_confirmation: error
                        }));
                      }}
                    />
                    {validationErrors.passwd_confirmation && (
                      <Form.Control.Feedback type="invalid">{validationErrors.passwd_confirmation}</Form.Control.Feedback>
                    )}
                  </FloatingLabel>
                </Form.Group>

                <Form.Group className="mb-3">
                  <button
                    type="submit"
                    className="btn btn-primary fw-medium py-2 px-3 w-100"
                  >
                    <div className="d-flex align-items-center justify-content-center py-1">
                      <span className="material-symbols-outlined fs-20 text-white me-2">
                        person_4
                      </span>
                      <span>Sign Up</span>
                    </div>
                  </button>
                </Form.Group>

                <Form.Group>
                  <p>
                    By confirming your email, you agree to our{" "}
                    <Link
                      to="/term-conditions/"
                      className="fw-medium text-decoration-none"
                    >
                      Terms of Service
                    </Link>{" "}
                    and that you have read and understood our{" "}
                    <Link
                      to="/privacy-policy/"
                      className="fw-medium text-decoration-none"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>

                  <p>
                    Already have an account.{" "}
                    <Link
                      to="/auth/sign-in/"
                      className="fw-medium text-primary text-decoration-none"
                    >
                      Sign In
                    </Link>
                  </p>
                </Form.Group>
              </Form>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SignUpForm;
